/**
 * What a document panel shows for one section's status.
 *
 * The generators store `generation_failed` for a section that could not be
 * written (EDTS stores `failed`), with the reasons in `failureReasons`
 * (EDTS: `generationError`). EDTS also stores `not_applicable`. The screen
 * says each of these in plain words and never shows the raw status string.
 *
 * REASONS (docgen reasons, 2026-09-30). The backend now writes a plain reason
 * for every failure caused by an exception, and puts the raw system text in a
 * separate field: `failureDetail` (EDTS: `generationErrorDetail`). See
 * functions/src/utils/sectionFailure.ts. A record that carries the detail
 * field is shown exactly as stored, with the detail in the tooltip; nothing is
 * guessed. Only an OLDER record, written before that rule, falls back to
 * `isSystemError`, which hides text that does not read as a plain sentence.
 */

export interface SectionStatusInput {
  status?: string | null;
  failureReasons?: unknown;
  /** Raw system text behind the failure; present on every record written under the rule. */
  failureDetail?: unknown;
  generationError?: unknown;
  /** EDTS: raw system text behind generationError. */
  generationErrorDetail?: unknown;
}

export interface SectionStatusView {
  kind: 'failed' | 'not_applicable' | 'ok';
  /** The badge text; '' when there is no status to show. */
  label: string;
  /**
   * The stored notice, one line per reason. Empty unless failed. The raw
   * system text, when there is any, is in `detail` for a tooltip.
   */
  notice: NoticeLine[];
  /** What to do about it. Present only when failed. */
  hint?: string;
}

export interface NoticeLine {
  text: string;
  /** The raw stored text, for a tooltip. */
  detail?: string;
}

export interface SectionStatusOptions {
  /** What to do about a failed section, naming the panel's real button. */
  regenerateHint?: string;
}

const FAILED = new Set(['generation_failed', 'failed']);

const LABELS: Record<string, string> = {
  generated: 'Generated',
  edited: 'Edited',
  reviewed: 'Reviewed',
  draft: 'Draft',
  approved: 'Approved',
  'in-review': 'In review',
  in_review: 'In review',
};

export const FAILED_LABEL = 'Could not be generated';
export const NOT_APPLICABLE_LABEL = 'Not applicable to this study';
/** Same words as SERVICE_FAILURE_REASON in functions/src/utils/sectionFailure.ts. */
export const SYSTEM_ERROR_TEXT = 'The generation service did not return an answer for this section.';

/** The hint for a panel whose regenerate button has this label. */
export function regenerateHint(buttonLabel: string): string {
  return `Use ${buttonLabel} to try this section again.`;
}

/** Most document panels carry a "Regenerate All Sections" button. */
export const REGENERATE_HINT = regenerateHint('Regenerate All Sections');

/**
 * The classes for a per-section regenerate icon (the MCS rows). It is
 * revealed on hover, except on a failed section, where the failure notice
 * points to it and it must be visible without hovering.
 */
export function regenerateIconClass(section: SectionStatusInput): string {
  return FAILED.has(String(section?.status ?? '').trim())
    ? 'opacity-100 transition-opacity'
    : 'opacity-0 group-hover:opacity-100 transition-opacity';
}

/**
 * FOR OLDER RECORDS ONLY: does this stored text NOT read as a plain sentence?
 *
 * Conservative on purpose. A plain sentence stays visible even when it
 * contains a code-like word (a field name such as AEGHOST, or INTERNAL); only
 * text with the marks of a system message is hidden:
 *   - one word with no spaces ("deadline-exceeded", "ECONNRESET")
 *   - the prefixes the old catch blocks wrote ("generation failed:", ...)
 *   - an exception name or a bracketed tag at the start ("TypeError:", "[...]")
 *   - an HTTP or service status number at the start ("429 ...", "14 UNAVAILABLE")
 *   - a stack frame, a file position, a URL or a Google API host
 *   - a network error code (ECONNRESET, ETIMEDOUT, ...)
 *   - JavaScript runtime wording ("of undefined", "is not a function",
 *     "(reading 'x')", a dotted name such as x.map)
 *   - the wording of the model service's own errors (fetch, JSON, quota,
 *     too many requests, resource exhausted, deadline, candidate blocked, ...)
 */
const NOT_A_SENTENCE: RegExp[] = [
  /^(?:generation failed|generation error|regeneration failed|unexpected error)\s*:/i,
  /^\s*\[?[A-Za-z.]*(?:Error|Exception)\b\]?\s*:/,
  /^\s*\[[^\]]+\]/,
  /^\s*\d{3}\b/,
  /^\s*\d{1,2}\s+[A-Z][A-Z_]{3,}\b/,
  /\[\d{3}\s[A-Za-z][A-Za-z ]*\]/,
  /^\s*at\s+\S.*:\d+:\d+\)?\s*$/m,
  /\.(?:js|ts|mjs|cjs):\d+/,
  /https?:\/\/|googleapis/i,
  /\bE(?:CONNRESET|CONNREFUSED|CONNABORTED|TIMEDOUT|NOTFOUND|AI_AGAIN|PIPE|HOSTUNREACH)\b/,
  /\bof (?:undefined|null)\b|\bis not (?:a function|a constructor|iterable|defined)\b|\(reading '[^']*'\)/,
  /\b[A-Za-z_$][\w$]*\.[A-Za-z_$][\w$]+\b/,
  /\b(?:fetch|json|socket|quota|deadline|timeout|timed out|too many requests|resource has been exhausted|rate[- ]limit|candidate was blocked|blocked due to|service unavailable|internal server error|status code|network error|unauthenticated|permission denied)\b/i,
];

export function isSystemError(text: string): boolean {
  const t = String(text ?? '').trim();
  if (!t) return false;
  if (!/\s/.test(t)) return true;
  return NOT_A_SENTENCE.some((re) => re.test(t));
}

const capitalise = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** An unknown code in words: "needs_human_check" -> "Needs human check". */
export function humanise(code: string): string {
  return capitalise(code.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase());
}

const strings = (raw: unknown): string[] =>
  (Array.isArray(raw) ? raw : typeof raw === 'string' ? [raw] : [])
    .filter((r): r is string => typeof r === 'string')
    .map((r) => r.trim())
    .filter(Boolean);

/**
 * A record written under the backend rule: the reasons exactly as stored,
 * trimmed, de-duplicated and capitalised. The detail goes on the line that
 * carries the service sentence, or on the first line when there is none.
 */
export function structuredNoticeLines(rawReasons: unknown, rawDetail: unknown): NoticeLine[] {
  const out: NoticeLine[] = [];
  for (const r of strings(rawReasons)) {
    const line = capitalise(r);
    if (!out.some((l) => l.text === line)) out.push({ text: line });
  }
  const detail = Array.from(new Set(strings(rawDetail))).join('\n');
  if (detail) {
    if (!out.length) out.push({ text: SYSTEM_ERROR_TEXT });
    const target = out.find((l) => l.text === SYSTEM_ERROR_TEXT) ?? out[0];
    target.detail = detail;
  }
  return out;
}

/**
 * An OLDER record: trimmed, de-duplicated, first letter capitalised. Text
 * that does not read as a plain sentence becomes one plain line carrying
 * every raw text in `detail`.
 */
export function noticeLines(raw: unknown): NoticeLine[] {
  const out: NoticeLine[] = [];
  for (const trimmed of strings(raw)) {
    if (isSystemError(trimmed)) {
      const existing = out.find((l) => l.text === SYSTEM_ERROR_TEXT);
      if (!existing) out.push({ text: SYSTEM_ERROR_TEXT, detail: trimmed });
      else if (!(existing.detail ?? '').split('\n').includes(trimmed)) {
        existing.detail = existing.detail ? `${existing.detail}\n${trimmed}` : trimmed;
      }
      continue;
    }
    const line = capitalise(trimmed);
    if (!out.some((l) => l.text === line)) out.push({ text: line });
  }
  return out;
}

function failureNotice(section: SectionStatusInput): NoticeLine[] {
  // The detail field is the mark of a record written under the rule.
  if (Array.isArray(section.failureDetail)) {
    const lines = structuredNoticeLines(section.failureReasons, section.failureDetail);
    if (lines.length) return lines;
  }
  if (typeof section.generationErrorDetail === 'string') {
    const lines = structuredNoticeLines(section.generationError, section.generationErrorDetail);
    if (lines.length) return lines;
  }
  const reasons = noticeLines(section.failureReasons);
  return reasons.length ? reasons : noticeLines(section.generationError);
}

export function sectionStatusView(section: SectionStatusInput, options: SectionStatusOptions = {}): SectionStatusView {
  const status = String(section?.status ?? '').trim();
  if (FAILED.has(status)) {
    return {
      kind: 'failed',
      label: FAILED_LABEL,
      notice: failureNotice(section),
      hint: options.regenerateHint || REGENERATE_HINT,
    };
  }
  if (status === 'not_applicable') {
    return { kind: 'not_applicable', label: NOT_APPLICABLE_LABEL, notice: [] };
  }
  if (!status) return { kind: 'ok', label: '', notice: [] };
  return { kind: 'ok', label: LABELS[status] ?? humanise(status), notice: [] };
}
